import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Trash2, Plus, Heart, FolderPlus, X, Folder } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { FavoritesGrid } from "@/components/FavoritesGrid";

export default function MyChats() {
  const navigate = useNavigate();
  const [chats, setChats] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [folders, setFolders] = useState([]);
  const [assign, setAssign] = useState({});
  const [activeFolder, setActiveFolder] = useState("all");
  const [loading, setLoading] = useState(true);

  const loadFolders = () => {
    api.get("/favorites/folders")
      .then((res) => { setFolders(res.data.folders || []); setAssign(res.data.assign || {}); })
      .catch(() => {});
  };

  const load = () => {
    api.get("/chats").then((res) => {
      setChats(res.data);
      setLoading(false);
    });
    api.get("/favorites").then((res) => setFavorites(res.data)).catch(() => {});
    loadFolders();
  };

  useEffect(load, []);

  const createFolder = async () => {
    const name = window.prompt("Name des neuen Ordners (z. B. Anime, Romantik):");
    if (!name || !name.trim()) return;
    try {
      await api.post("/favorites/folders", { name: name.trim() });
      toast.success("Ordner erstellt");
      loadFolders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Ordner nicht erstellen");
    }
  };

  const deleteFolder = async (e, fid) => {
    e.stopPropagation();
    if (!window.confirm("Diesen Ordner löschen? Die Favoriten bleiben erhalten.")) return;
    try {
      await api.delete(`/favorites/folders/${fid}`);
      if (activeFolder === fid) setActiveFolder("all");
      loadFolders();
    } catch (_) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const assignFolder = async (charId, folderId) => {
    setAssign((a) => {
      const next = { ...a };
      if (folderId) next[charId] = folderId; else delete next[charId];
      return next;
    });
    try {
      await api.put(`/favorites/${charId}/folder`, { folder_id: folderId });
      loadFolders();
    } catch (_) {
      toast.error("Zuordnung fehlgeschlagen");
      loadFolders();
    }
  };

  const remove = async (e, sid) => {
    e.stopPropagation();
    await api.delete(`/chats/${sid}`);
    toast.success("Chat deleted");
    setChats((c) => c.filter((x) => x.session_id !== sid));
  };

  const shownFavorites = activeFolder === "all"
    ? favorites
    : favorites.filter((c) => assign[c.id] === activeFolder);

  return (
    <div className="min-h-screen bg-[#090A0F] grid-bg">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">
        {favorites.length > 0 && (
          <div className="mb-12" data-testid="mychats-favorites">
            <div className="flex items-center gap-2 mb-4">
              <Heart className="w-5 h-5 text-rose-400 fill-rose-400" />
              <h2 className="font-display text-xl sm:text-2xl font-bold text-white">Deine Favoriten</h2>
              <span className="text-sm text-slate-500">({favorites.length})</span>
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-5" data-testid="fav-folder-tabs">
              <button
                data-testid="fav-folder-all"
                onClick={() => setActiveFolder("all")}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                  activeFolder === "all"
                    ? "bg-rose-500/20 text-rose-200 border border-rose-400/40"
                    : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 border border-white/5"
                }`}
              >
                <Heart className="w-3.5 h-3.5" /> Alle ({favorites.length})
              </button>
              {folders.map((f) => (
                <div
                  key={f.id}
                  role="button"
                  tabIndex={0}
                  data-testid={`fav-folder-${f.id}`}
                  onClick={() => setActiveFolder(f.id)}
                  className={`group flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all ${
                    activeFolder === f.id
                      ? "bg-fuchsia-500/20 text-fuchsia-200 border border-fuchsia-400/40"
                      : "bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 border border-white/5"
                  }`}
                >
                  <Folder className="w-3.5 h-3.5" /> {f.name} ({f.count})
                  <button
                    type="button"
                    data-testid={`fav-folder-delete-${f.id}`}
                    onClick={(e) => deleteFolder(e, f.id)}
                    className="ml-0.5 opacity-50 hover:opacity-100 hover:text-rose-400"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              <button
                data-testid="fav-folder-create"
                onClick={createFolder}
                className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 border border-dashed border-white/15 transition-all"
              >
                <FolderPlus className="w-3.5 h-3.5" /> Ordner
              </button>
            </div>

            {shownFavorites.length === 0 ? (
              <p className="text-sm text-slate-500 py-6" data-testid="fav-folder-empty">
                Noch keine Favoriten in diesem Ordner. Weise unten Charakteren einen Ordner zu.
              </p>
            ) : (
              <FavoritesGrid
                favorites={shownFavorites}
                onChange={setFavorites}
                folders={folders}
                assign={assign}
                onAssign={assignFolder}
                reorderable={activeFolder === "all"}
              />
            )}
          </div>
        )}

        <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mb-1">My Chats</h1>
        <p className="text-slate-400 text-sm mb-8">Continue where you left off</p>

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-20 rounded-2xl bg-white/5 animate-pulse" />
            ))}
          </div>
        ) : chats.length === 0 ? (
          <div className="text-center py-20" data-testid="no-chats">
            <MessageCircle className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400 mb-6">You haven't started any chats yet.</p>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white font-semibold rounded-full px-6 py-3 shadow-lg shadow-violet-500/20 active:scale-95 transition-all"
            >
              <Plus className="w-4 h-4" /> Explore characters
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {chats.map((c) => (
              <div
                key={c.session_id}
                data-testid={`chat-item-${c.session_id}`}
                onClick={() => navigate(`/chat/${c.session_id}`)}
                className="group flex items-center gap-4 bg-[#131520] hover:bg-[#1A1D2C] border border-white/5 hover:border-fuchsia-500/30 rounded-2xl p-4 cursor-pointer transition-all"
              >
                <img
                  src={c.character_avatar}
                  alt={c.character_name}
                  className="w-14 h-14 rounded-xl object-cover border border-white/10 flex-shrink-0"
                />
                <div className="min-w-0 flex-1">
                  <h3 className="font-display font-semibold text-white truncate">{c.character_name}</h3>
                  <p className="text-sm text-slate-400 truncate">{c.last_message || "Start chatting..."}</p>
                </div>
                <button
                  data-testid={`delete-chat-${c.session_id}`}
                  onClick={(e) => remove(e, c.session_id)}
                  className="p-2 rounded-full text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-all opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
